import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  'Fractional COO & Operations Advisory',
  'Support Tech Stack & Workflow Optimization (4-Week Sprint)',
  'Voice of the Customer & De-Escalation Frameworks',
  'General CX Strategy & Leadership',
  'Team Management & Optimization',
  'All of the above',
  'Other'
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    industry: '',
    service_interest: [],
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const toggleService = (opt) => {
    setFormData((prev) => ({
      ...prev,
      service_interest: prev.service_interest.includes(opt)
        ? prev.service_interest.filter((s) => s !== opt)
        : [...prev.service_interest, opt]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.service_interest.length === 0) {
      toast.error('Please select at least one', {
        description: 'Choose your primary challenge / service interest.'
      });
      return;
    }
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast.success("Message Sent!", {
          description: response.data.message,
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          industry: '',
          service_interest: [],
          message: ''
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : "Something went wrong. Please try again.";
      toast.error("Error", {
        description: message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-20 md:py-32 bg-white">
      <div className="container mx-auto px-[7.6923%]">
        {/* Header */}
        <div className="text-center mb-20">
          <h1 className="text-[36px] sm:text-[48px] lg:text-[66px] font-semibold leading-[1.1] tracking-[-0.62px] text-gray-900 mb-6">
            Let's Build Something Great
          </h1>
          <p className="text-[20px] text-gray-900/85 max-w-3xl mx-auto">
            Ready to transform your customer experience? Get in touch and let's discuss how we can help you build a world-class CX department.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <div className="space-y-12">
            <div>
              <h2 className="text-[24px] md:text-[32px] font-semibold text-gray-900 mb-8">
                Get In Touch
              </h2>
              <p className="text-[18px] text-gray-900/85 leading-[1.6] mb-12">
                Whether you're looking to build a new CX department from scratch or optimize your existing operations, we're here to help. Schedule a consultation to discuss your specific needs.
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex items-start gap-6">
                <div className="bg-gray-50 p-4 border border-gray-200">
                  <Mail className="w-8 h-8 text-[#00D4B4]" />
                </div>
                <div>
                  <div className="text-[20px] font-medium text-gray-900 mb-2">Email</div>
                  <a href="mailto:cxsagepro@gmail.com" className="text-[18px] text-gray-900/85 hover:text-[#00D4B4] transition-colors">
                    cxsagepro@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="bg-gray-50 p-4 border border-gray-200">
                  <Phone className="w-8 h-8 text-[#00D4B4]" />
                </div>
                <div>
                  <div className="text-[20px] font-medium text-gray-900 mb-2">Phone</div>
                  <div className="text-[18px] text-gray-900/85">
                    Available upon request
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="bg-gray-50 p-4 border border-gray-200">
                  <MapPin className="w-8 h-8 text-[#00D4B4]" />
                </div>
                <div>
                  <div className="text-[20px] font-medium text-gray-900 mb-2">Service Areas</div>
                  <div className="text-[18px] text-gray-900/85">
                    Remote consulting available globally
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-10 border border-gray-200 mt-12">
              <h3 className="text-[24px] font-semibold text-gray-900 mb-4">
                Response Time
              </h3>
              <p className="text-[18px] text-gray-900/85 leading-[1.6]">
                We typically respond to all inquiries within 24 hours during business days. For urgent matters, please mention it in your message.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gray-50 p-6 sm:p-12 border border-gray-200">
            <h2 className="text-[24px] md:text-[32px] font-semibold text-gray-900 mb-8">
              Send a Message
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-[18px] font-medium text-gray-900 mb-3">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full bg-white border border-gray-300 text-gray-900 px-6 py-4 text-[18px] focus:outline-none focus:border-[#00D4B4] transition-colors"
                  style={{ borderRadius: '0px' }}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-[18px] font-medium text-gray-900 mb-3">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-white border border-gray-300 text-gray-900 px-6 py-4 text-[18px] focus:outline-none focus:border-[#00D4B4] transition-colors"
                  style={{ borderRadius: '0px' }}
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-[18px] font-medium text-gray-900 mb-3">
                  Company Name *
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  required
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full bg-white border border-gray-300 text-gray-900 px-6 py-4 text-[18px] focus:outline-none focus:border-[#00D4B4] transition-colors"
                  style={{ borderRadius: '0px' }}
                  placeholder="Your Company"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-[18px] font-medium text-gray-900 mb-3">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-white border border-gray-300 text-gray-900 px-6 py-4 text-[18px] focus:outline-none focus:border-[#00D4B4] transition-colors"
                  style={{ borderRadius: '0px' }}
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="industry" className="block text-[18px] font-medium text-gray-900 mb-3">
                  Industry Sector *
                </label>
                <select
                  id="industry"
                  name="industry"
                  required
                  value={formData.industry}
                  onChange={handleChange}
                  className="w-full bg-white border border-gray-300 text-gray-900 px-6 py-4 text-[18px] focus:outline-none focus:border-[#00D4B4] transition-colors"
                  style={{ borderRadius: '0px' }}
                >
                  <option value="">Select your industry</option>
                  <option value="saas">SaaS</option>
                  <option value="ecommerce">eCommerce</option>
                  <option value="auction">Auction Technology</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-[18px] font-medium text-gray-900 mb-3">
                  Primary Challenge / Service Interest *
                </label>
                <div className="border border-gray-300" data-testid="service-interest">
                  <button
                    type="button"
                    onClick={() => setServiceOpen((o) => !o)}
                    data-testid="service-interest-toggle"
                    className="w-full flex items-center justify-between px-6 py-4 text-left text-[18px] text-gray-900"
                  >
                    <span className={formData.service_interest.length ? 'text-gray-900' : 'text-gray-400'}>
                      {formData.service_interest.length ? `${formData.service_interest.length} selected` : 'Select one or more'}
                    </span>
                    <ChevronDown className={`w-5 h-5 transition-transform ${serviceOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {serviceOpen && (
                    <div className="border-t border-gray-200 p-2">
                      {SERVICE_OPTIONS.map((opt) => (
                        <label
                          key={opt}
                          data-testid={`service-opt-${opt}`}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.service_interest.includes(opt)}
                            onChange={() => toggleService(opt)}
                            className="w-4 h-4 accent-[#00D4B4]"
                          />
                          <span className="text-[16px] text-gray-800">{opt}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {formData.service_interest.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {formData.service_interest.map((s) => (
                      <span key={s} className="inline-flex items-center bg-[#E1F5EE] text-[#085041] text-sm px-3 py-1">{s}</span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="message" className="block text-[18px] font-medium text-gray-900 mb-3">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  minLength={10}
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  className="w-full bg-white border border-gray-300 text-gray-900 px-6 py-4 text-[18px] focus:outline-none focus:border-[#00D4B4] transition-colors resize-none"
                  style={{ borderRadius: '0px' }}
                  placeholder="Tell us about your CX challenges and goals..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#00D4B4] text-black px-8 py-5 text-lg font-medium hover:bg-[#00D4B4]/10 hover:text-[#00D4B4] transition-all duration-400 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: '0px' }}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                <Send className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>

        {/* About the Founder */}
        <div className="mt-32" data-testid="founder-section">
          <div className="bg-gray-50 border border-gray-200 p-12 lg:p-16">
            <div className="flex flex-col md:flex-row gap-10 items-start">
              <div className="flex-shrink-0">
                <div className="w-28 h-28 bg-[#00D4B4] flex items-center justify-center">
                  <span className="text-white text-[40px] font-bold">PL</span>
                </div>
              </div>
              <div>
                <h2 className="text-[24px] md:text-[32px] font-semibold text-gray-900 mb-2">About the Founder</h2>
                <div className="text-[18px] font-medium text-[#00D4B4] mb-6">Phillip Labossiere</div>
                <p className="text-[18px] text-gray-900/85 leading-[1.7] mb-6">
                  I'm Phillip Labossiere, a customer operations and experience leader with 15+ years building CX departments for SaaS, eCommerce, and PE-backed companies. My background includes leadership roles at Auction Technology Group, Auction Mobility, and LiveAuctioneers, where I scaled support teams, cut resolution times, and turned CX into a measurable revenue driver.
                </p>
                <p className="text-[18px] text-gray-900/85 leading-[1.7]">
                  I hold a BS in Computer Science and Business Management from NYIT, I'm Lean Six Sigma certified and hold Google's AI Professional Certificate, and I'm a 5x Stevie Award winner. CX Sage is where I bring that same experience to companies who want their support operation to be a competitive advantage, not a cost center.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;